import {useState} from 'react';
import {format} from 'date-fns';
import {Loader2, Plus} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Textarea} from '@common-web/components/ui/textarea';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {CODE_PERMISSIONS} from '@common/permissions';

export function GroupNotes({vbaGroupId}: {vbaGroupId: string}) {
  const canManage = useHasPermission(CODE_PERMISSIONS.groupPipeline.manage.key);
  const utils = trpc.useUtils();
  const notes = trpc.internal.groupPipeline.notes.list.useQuery({vbaGroupId});
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');

  const add = trpc.internal.groupPipeline.notes.add.useMutation({
    onSuccess: () => {
      setBody('');
      setComposing(false);
      return utils.internal.groupPipeline.notes.list.invalidate({vbaGroupId});
    },
    onError: createMutationErrorHandler('Could not add the note'),
  });

  return (
    <section className='rounded-lg border bg-white p-5'>
      <div className='mb-4 flex items-center justify-between gap-2'>
        <h2 className='text-base font-semibold'>Notes</h2>
        {canManage && !composing && (
          <Button
            variant='outline'
            size='sm'
            className='bg-white'
            onClick={() => setComposing(true)}
          >
            <Plus className='mr-1 h-4 w-4' />
            Add
          </Button>
        )}
      </div>

      {composing && (
        <div className='mb-4 space-y-2'>
          <Textarea
            className='bg-white'
            autoFocus
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder='Called the employer about the bank letter'
            rows={3}
          />
          <div className='flex items-center justify-between gap-2'>
            <span className='text-xs text-gray-500'>Visible to CBS only</span>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                className='bg-white'
                onClick={() => {
                  setBody('');
                  setComposing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size='sm'
                disabled={!body.trim() || add.isPending}
                onClick={() => add.mutate({vbaGroupId, body: body.trim()})}
              >
                Save note
              </Button>
            </div>
          </div>
        </div>
      )}

      {notes.isLoading ? (
        <Loader2 className='mx-auto my-4 h-5 w-5 animate-spin text-muted-foreground' />
      ) : !notes.data?.length ? (
        !composing && <p className='text-sm text-muted-foreground'>No notes yet.</p>
      ) : (
        <ul>
          {notes.data.map((note) => (
            <li key={note.id} className='border-b py-3 first:pt-0 last:border-0 last:pb-0'>
              <div className='mb-1 flex flex-wrap items-center gap-x-2 text-sm'>
                <span className='font-medium'>{note.author}</span>
                <span
                  className='text-gray-500'
                  title={format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                >
                  {format(new Date(note.createdAt), 'MMM d, yyyy')}
                </span>
                {note.visibility === 'SHARED' && (
                  <span className='rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600'>
                    Shared with tenant
                  </span>
                )}
              </div>
              <p className='whitespace-pre-wrap text-sm text-gray-800'>{note.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
